import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { scrollToId } from "../lib/smoothScroll";
import { links } from "../lib/contact";

const NAV = [
  { label: "Home", id: "home" },
  { label: "Our Services", id: "services" },
  { label: "Why Recovery Rides", id: "why" },
  { label: "How It Works", id: "how" },
  { label: "Service Area", id: "area" },
  { label: "About Us", id: "about" },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_6px_30px_-12px_rgba(59,58,78,0.25)]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-[68px]">
          <button
            onClick={() => go("home")}
            className="flex items-center focus:outline-none focus:ring-4 focus:ring-teal-200 rounded-lg"
            aria-label="Recovery Rides home"
            data-testid="nav-logo"
          >
            <img
              src="/brand/logo-horizontal.webp"
              alt="Recovery Rides LLC logo"
              className="h-9 sm:h-10 w-auto"
            />
          </button>

          <div className="hidden xl:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="px-3.5 py-2 text-sm font-medium text-[#3B3A4E]/80 rounded-full transition-colors hover:text-[#0891B2] hover:bg-[#1AD1B9]/8 focus:outline-none focus:ring-2 focus:ring-teal-200"
                data-testid={`nav-link-${n.id}`}
              >
                {n.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a
              href={links.call}
              className="hidden sm:inline-flex xl:hidden items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-[#0891B2] border border-[#00B4B4]/25 hover:bg-[#1AD1B9]/8 transition-colors"
              data-testid="nav-call-quick"
            >
              <Phone size={15} /> Call
            </a>
            <button
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white brand-gradient shadow-md shadow-teal-500/25 transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-teal-300/50"
              data-testid="nav-request-service"
            >
              Request Service
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full text-[#3B3A4E] hover:bg-[#3B3A4E]/6 focus:outline-none focus:ring-2 focus:ring-teal-200"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="nav-menu-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-[#3B3A4E]/8"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 flex flex-col">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  className="text-left px-3 py-3 text-base font-medium text-[#3B3A4E] rounded-xl hover:bg-[#1AD1B9]/8 hover:text-[#0891B2] transition-colors"
                  data-testid={`mobile-nav-${n.id}`}
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => go("contact")}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-base font-semibold text-white brand-gradient"
                data-testid="mobile-request-service"
              >
                Request Service
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
